import React, { useCallback, useMemo } from 'react';
import GridLayout from 'react-grid-layout';
import './Dashboard.css';
import { CustomTile, TileDefinition } from '../../../types/dashboard';
import DashboardGridCard from './DashboardGridCard';
import { updateTileLayout } from './DashboardGridHelpers';

interface DashboardGridCardProps {
  selectedDashboard: string | null;
  tiles: TileDefinition[];
  width: number;
  handleUpdateDashboard: (dashboardId: string, updatedTiles: TileDefinition[]) => void;
  setTileErrors: React.Dispatch<
    React.SetStateAction<{ [tileId: CustomTile['id']]: string | null }>
  >;
}

const DashboardGridLayout: React.FC<DashboardGridCardProps> = ({
                                                                 selectedDashboard,
                                                                 tiles,
                                                                 width,
                                                                 handleUpdateDashboard,
                                                                 setTileErrors,
                                                               }) => {
  const handleRemoveTile = (tileId: string) => {
    if (!selectedDashboard) return;

    const updatedTiles = (tiles ?? []).filter(
      (tile) => String(tile.id) !== String(tileId)
    );

    handleUpdateDashboard(selectedDashboard, updatedTiles);
  };

  const handleUpdateDashboardCard = (updatedTile: TileDefinition) => {
    if (!selectedDashboard) return;

    handleUpdateDashboard(
      selectedDashboard,
      (tiles ?? []).map((tile) =>
        String(tile.id) === String(updatedTile.id) ? updatedTile : tile
      )
    );
  };

  const handleTileError = useCallback(
    (id: number, error: string | null) => {
      setTileErrors((prev) => {
        const prevVal = prev[id] ?? null;
        const nextVal = error ?? null;
        if (prevVal === nextVal) return prev;
        return { ...prev, [id]: nextVal };
      });
    },
    [setTileErrors]
  );

  const selectedDashboardLayout = useMemo(() => {
    return tiles.map((tile, idx) => {
      const rawX = Number(tile.layout?.x);
      const rawY = Number(tile.layout?.y);
      const rawW = Number(tile.layout?.w);
      const rawH = Number(tile.layout?.h);

      const w = Number.isFinite(rawW) && rawW > 0 ? Math.min(rawW, 8) : 4;
      const h = Number.isFinite(rawH) && rawH > 0 ? rawH : 4;
      const x = Number.isFinite(rawX)
        ? Math.max(0, Math.min(rawX, 8 - w))
        : (idx % 2) * 4;
      const y = Number.isFinite(rawY)
        ? Math.max(0, rawY)
        : Math.floor(idx / 2) * 4;

      return {
        i: String(tile.id),
        x,
        y,
        w,
        h,
        minH: 1,
        minW: 1,
        maxH: 50,
        maxW: 8,
      };
    });
  }, [tiles]);

  const handleLayoutStop = useCallback(
    (layout: any[]) => {
      if (!selectedDashboard) return;

      const layoutById = new Map<string, any>(
        layout.map((l) => [String(l.i), l])
      );

      let changed = false;

      const updatedTiles: TileDefinition[] = (tiles || []).map((current: TileDefinition) => {
        const l = layoutById.get(String(current.id));
        if (!l) return current;

        const { tile, updated } = updateTileLayout(current, l);
        if (updated) changed = true;
        return tile;
      });

      if (!changed) return;

      handleUpdateDashboard(selectedDashboard, updatedTiles);
    },
    [tiles, handleUpdateDashboard, selectedDashboard]
  );

  return (
    <GridLayout
      className="layout min-w-[800px]"
      layout={selectedDashboardLayout}
      cols={8}
      rowHeight={50}
      width={width}
      draggableHandle=".drag-handle"

      /*
        Important:
        Do NOT cancel .tile-header or .tile-actions because the drag handle lives
        inside that area. Only cancel explicitly marked interactive controls.
      */
      draggableCancel=".dashboard-tile-interactive, .dashboard-tile-interactive *"

      isBounded
      compactType="vertical"
      containerPadding={[0, 0]}
      margin={[8, 8] as any}
      onDragStop={(layout) => handleLayoutStop(layout)}
      onResizeStop={(layout) => handleLayoutStop(layout)}
      isDraggable={true}
      isResizable={true}
    >
      {(tiles ?? []).map((tile, idx) => (
        <div key={tile.id} data-grid={selectedDashboardLayout[idx]}>
          <DashboardGridCard
            key={tile.id}
            tile={tile}
            handleUpdateDashboardCard={handleUpdateDashboardCard}
            handleRemoveTile={handleRemoveTile}
            handleTileError={handleTileError}
            width={width}
            selectedDashboardId={selectedDashboard}
          />
        </div>
      ))}
    </GridLayout>
  );
};

export default DashboardGridLayout;